// @ts-nocheck
import { useShellContext } from "@insite/client-framework/Components/IsInShell";
import ga from "@store/Framework/Common/GoogleAnalytics";
import constants from "@store/Framework/Common/GoogleAnalytics/constants";
import useIsFirstRender from "@store/Framework/Common/Hooks/useIsFirstRender";
import { useSettingsCollection } from "@store/Framework/Store/Context/ContextHooks";
import isObjectLike from "lodash/isObjectLike";
import { FC, useEffect, useState } from "react";

const allowedTrackingTypes = [constants.trackingType.gtm, constants.trackingType.ga4];
const logMessage = "DataLayer Push:";
let pauseTracking = false;
let eventQueue: any[] = [];
let count = 0;

export interface GoogleAnalyticsProps {}

type Props = GoogleAnalyticsProps;

const GoogleAnalytics: FC<Props> = () => {
    const { isInShell } = useShellContext();
    const clientId = ga.use.clientId();
    const currency = ga.use.currency();
    const isFirstRender = useIsFirstRender();
    const language = ga.use.language();
    const trackPageView = ga.use.trackPageView();
    const [trackToggle, setTrackToggle] = useState(count);

    const {
        extensionWebsiteSettings,
        websiteSettings: { googleTrackingTypeComputed },
    } = useSettingsCollection();

    const isEnabled =
        (extensionWebsiteSettings?.enableEnhancedAnalytics ?? ga.enabled) &&
        allowedTrackingTypes.includes(googleTrackingTypeComputed);

    const isDebug = isEnabled && (extensionWebsiteSettings?.enableEnhancedAnalyticsDebug ?? ga.debug);
    const allowTracking = isEnabled && !isInShell && ((typeof window !== "undefined" && window.dataLayer) || isDebug);

    useEffect(() => {
        ga.trackingType = googleTrackingTypeComputed;
    }, [googleTrackingTypeComputed]);

    useEffect(() => {
        ga.enabled = isEnabled;
    }, [isEnabled]);

    useEffect(() => {
        ga.debug = isDebug;
    }, [isDebug]);

    useEffect(() => {
        if (!isFirstRender) {
            pauseTracking = false;

            const vpvIndex = eventQueue.findIndex(x => x.event === "virtualPageView");
            if (vpvIndex >= 0) {
                ga.push(eventQueue.splice(vpvIndex, 1)[0]);
                trackPageView();
            }

            eventQueue.forEach(ga.push);
            eventQueue = [];
            count = count > 999999 ? 0 : count;
        }
    }, [trackToggle]);

    useEffect(() => {
        if (allowTracking) {
            ga.delayTracking = (fn?: () => any, timeout?: number) => {
                pauseTracking = true;
                setTimeout(() => {
                    fn?.();
                    setTrackToggle(++count);
                }, timeout);
            };

            const baseDataLayer: any = window.dataLayer || [];

            if (isDebug) {
                window.dataLayer?.forEach(x => ga.util.logDebug(logMessage, x));
            }

            baseDataLayer.push = new Proxy(baseDataLayer.push, {
                apply: (target, thisArg, argumentsList) => {
                    const values = argumentsList.reduce((result, value) => {
                        if (isObjectLike(value)) {
                            if (!value.skipProxy) {
                                if (value.event === "virtualPageView") {
                                    eventQueue.push({ ...value, skipProxy: true });
                                    ga.delayTracking(); // ensure page meta data is updated

                                    return result;
                                }

                                if (pauseTracking) {
                                    if (value.isClear) {
                                        return result;
                                    }

                                    if (value.event || value[0] === "event") {
                                        eventQueue.push(value);

                                        return result;
                                    }
                                }
                            }

                            delete value.isClear;
                            delete value.skipProxy;

                            ga.util.cleanupObject(value);

                            if (isDebug && !value.skipDebug) {
                                ga.util.logDebug(logMessage, value);
                            }

                            delete value.skipDebug;
                        }

                        return [...result, value];
                    }, []);

                    if (!values.length) {
                        return true;
                    }

                    return target.apply(thisArg, values);
                },
            });
        }
    }, []);

    useEffect(() => {
        ga.push("set", { clientId, currency, language });
    }, [clientId, currency, language]);

    return null;
};

export default GoogleAnalytics;
